import type {
  ChangeEvent,
  MouseEvent,
  TouchEvent,
} from "react";
import { useState, useRef, useEffect } from "react";
import {
  Image20Regular,
  Delete20Regular,
  Dismiss20Regular,
  Checkmark20Regular,
} from "@fluentui/react-icons";

interface AvatarUploaderProps {
  currentImage?: string | null;
  onImageChange: (file: File | null) => void;
  label?: string;
  subtitle?: string;
  size?: "small" | "medium" | "large";
  variant?: "entity" | "person"; // entity = square, person = circular
}

interface ImageCropState {
  zoom: number;
  positionX: number;
  positionY: number;
}

export function AvatarUploader({
  currentImage,
  onImageChange,
  label = "Logotipo",
  subtitle = "Formatos suportados: JPG, PNG. Tamanho recomendado: 200×200.",
  size = "medium",
  variant = "entity", // Default to entity (square) for businesses
}: AvatarUploaderProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentImage || null,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [cropState, setCropState] = useState<ImageCropState>({
    zoom: 1,
    positionX: 0,
    positionY: 0,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const cropContainerRef = useRef<HTMLDivElement>(null);

  const sizeClasses = {
    small: "w-16 h-16",
    medium: "w-24 h-24",
    large: "w-32 h-32",
  };
  const previewRadius =
    variant === "person" ? "var(--radius-full)" : "var(--radius)";

  useEffect(() => {
    setPreviewUrl(currentImage || null);
  }, [currentImage]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Por favor, selecione apenas ficheiros de imagem (JPG, PNG).");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("O ficheiro é muito grande. Tamanho máximo: 5MB.");
        return;
      }

      // Create temporary URL for crop editor
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageUrl(reader.result as string);
        setCropState({ zoom: 1, positionX: 0, positionY: 0 });
        setIsEditing(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    onImageChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleCancelCrop = () => {
    setIsEditing(false);
    setTempImageUrl(null);
    setCropState({ zoom: 1, positionX: 0, positionY: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleConfirmCrop = () => {
    if (!imageRef.current || !tempImageUrl) return;

    // Create canvas for cropping
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const cropSize = 200; // Output size
    canvas.width = cropSize;
    canvas.height = cropSize;

    // Fill with transparent background
    ctx.clearRect(0, 0, cropSize, cropSize);

    // Create temp image for cropping
    const img = new Image();
    img.onload = () => {
      // Get the container dimensions
      const containerWidth = cropContainerRef.current?.offsetWidth || 400;
      const containerHeight = cropContainerRef.current?.offsetHeight || 400;

      // Calculate how the image fits in the container (object-contain)
      const imgAspect = img.width / img.height;
      const containerAspect = containerWidth / containerHeight;
      
      let displayWidth, displayHeight;
      if (imgAspect > containerAspect) {
        displayWidth = containerWidth;
        displayHeight = containerWidth / imgAspect;
      } else {
        displayHeight = containerHeight;
        displayWidth = containerHeight * imgAspect;
      }

      // Scale from display size to actual image size
      const scaleX = img.width / displayWidth;
      const scaleY = img.height / displayHeight;

      // Calculate the visible area in the container after zoom and position
      const zoom = cropState.zoom;
      
      // The position is in CSS pixels, convert to image pixels
      const offsetX = -cropState.positionX * scaleX;
      const offsetY = -cropState.positionY * scaleY;

      // Calculate the crop area in the original image
      // The crop area is the center of the zoomed image
      const cropWidth = (displayWidth / zoom) * scaleX;
      const cropHeight = (displayHeight / zoom) * scaleY;
      
      const startX = (img.width - cropWidth) / 2 + offsetX;
      const startY = (img.height - cropHeight) / 2 + offsetY;

      // Calculate aspect ratio of the cropped area
      const croppedAspect = cropWidth / cropHeight;
      
      // Calculate final dimensions to fit in canvas while maintaining aspect ratio
      let finalWidth, finalHeight, finalX, finalY;
      
      if (croppedAspect > 1) {
        // Wider than tall - fit to width
        finalWidth = cropSize;
        finalHeight = cropSize / croppedAspect;
        finalX = 0;
        finalY = (cropSize - finalHeight) / 2;
      } else {
        // Taller than wide - fit to height
        finalHeight = cropSize;
        finalWidth = cropSize * croppedAspect;
        finalX = (cropSize - finalWidth) / 2;
        finalY = 0;
      }

      // Draw the cropped and scaled image, centered with correct aspect ratio
      ctx.drawImage(
        img,
        startX,
        startY,
        cropWidth,
        cropHeight,
        finalX,
        finalY,
        finalWidth,
        finalHeight
      );

      // Convert canvas to blob and then to file
      canvas.toBlob((blob) => {
        if (blob) {
          const croppedFile = new File([blob], "avatar.png", {
            type: "image/png",
          });
          const croppedUrl = canvas.toDataURL("image/png");
          setPreviewUrl(croppedUrl);
          onImageChange(croppedFile);
          setIsEditing(false);
          setTempImageUrl(null);
        }
      }, "image/png");
    };
    img.src = tempImageUrl;
  };

  const handleMouseDown = (e: MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    setCropState((prev) => ({
      ...prev,
      positionX: prev.positionX + deltaX / 2,
      positionY: prev.positionY + deltaY / 2,
    }));

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStart.x;
    const deltaY = touch.clientY - dragStart.y;

    setCropState((prev) => ({
      ...prev,
      positionX: prev.positionX + deltaX / 2,
      positionY: prev.positionY + deltaY / 2,
    }));

    setDragStart({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.body.style.cursor = "grabbing";
    } else {
      document.body.style.cursor = "";
    }

    return () => {
      document.body.style.cursor = "";
    };
  }, [isDragging]);

  return (
    <div>
      {label && (
        <label
          className="block text-foreground mb-2"
          style={{
            fontSize: "var(--text-caption)",
            fontWeight: "var(--font-weight-semi-bold)",
          }}
        >
          {label}
        </label>
      )}

      {subtitle && (
        <p
          className="text-secondary-text mb-3"
          style={{
            fontSize: "var(--text-caption)",
          }}
        >
          {subtitle}
        </p>
      )}

      <div className="flex items-center gap-4">
        {/* Avatar Preview */}
        <div
          className={`${sizeClasses[size]} rounded-lg border-2 border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden flex-shrink-0 relative group`}
          style={{
            borderRadius: previewRadius,
          }}
        >
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-full object-contain"
            />
          ) : (
            <Image20Regular className="w-8 h-8 text-secondary-text" />
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleClick}
            className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
            style={{
              borderRadius: "var(--radius-button)",
              fontSize: "var(--text-base)",
            }}
          >
            <Image20Regular className="w-5 h-5" />
            {previewUrl ? "Alterar imagem" : "Carregar imagem"}
          </button>

          {previewUrl && (
            <button
              type="button"
              onClick={handleRemove}
              className="px-4 py-2 bg-secondary hover:bg-destructive/10 border border-border hover:border-destructive/50 rounded transition-colors flex items-center gap-2 text-destructive min-h-[44px]"
              style={{
                borderRadius: "var(--radius-button)",
                fontSize: "var(--text-base)",
              }}
            >
              <Delete20Regular className="w-5 h-5" />
              Remover
            </button>
          )}
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png, image/jpeg, image/jpg"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Crop Editor Modal */}
      {isEditing && tempImageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={handleCancelCrop}
        >
          <div
            className="bg-card border border-border p-6 max-w-md w-full mx-4"
            style={{
              borderRadius: "var(--radius-card)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3
                className="text-foreground"
                style={{
                  fontSize: "var(--text-section-title)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Ajustar Imagem
              </h3>
              <button
                onClick={handleCancelCrop}
                className="text-secondary-text hover:text-foreground transition-colors"
              >
                <Dismiss20Regular className="w-5 h-5" />
              </button>
            </div>

            {/* Crop Area */}
            <div
              ref={cropContainerRef}
              className="relative w-full aspect-square bg-muted/30 rounded overflow-hidden mb-4 border-2 border-border"
              style={{
                borderRadius: previewRadius,
                cursor: isDragging ? "grabbing" : "grab",
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <img
                ref={imageRef}
                src={tempImageUrl}
                alt="Crop preview"
                className="absolute inset-0 w-full h-full object-contain select-none"
                style={{
                  transform: `scale(${cropState.zoom}) translate(${cropState.positionX}px, ${cropState.positionY}px)`,
                  transformOrigin: "center",
                  transition: isDragging ? "none" : "transform 0.1s ease-out",
                }}
                draggable={false}
              />
            </div>

            {/* Zoom Slider */}
            <div className="mb-6">
              <label
                className="block text-foreground mb-2"
                style={{
                  fontSize: "var(--text-caption)",
                  fontWeight: "var(--font-weight-semi-bold)",
                }}
              >
                Zoom
              </label>
              <input
                type="range"
                min="1"
                max="3"
                step="0.1"
                value={cropState.zoom}
                onChange={(e) =>
                  setCropState((prev) => ({
                    ...prev,
                    zoom: parseFloat(e.target.value),
                  }))
                }
                className="w-full h-2 appearance-none cursor-pointer"
                style={{
                  borderRadius: "var(--radius)",
                  background: `linear-gradient(to right, var(--primary) 0%, var(--primary) ${
                    ((cropState.zoom - 1) / (3 - 1)) * 100
                  }%, var(--muted) ${
                    ((cropState.zoom - 1) / (3 - 1)) * 100
                  }%, var(--muted) 100%)`,
                }}
              />
              <div className="flex justify-between mt-1">
                <span
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-caption)" }}
                >
                  1×
                </span>
                <span
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-caption)" }}
                >
                  {cropState.zoom.toFixed(1)}×
                </span>
                <span
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-caption)" }}
                >
                  3×
                </span>
              </div>
            </div>

            {/* Helper Text */}
            <p
              className="text-secondary-text text-center mb-4"
              style={{
                fontSize: "var(--text-caption)",
              }}
            >
              Arraste a imagem para reposicionar
            </p>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCancelCrop}
                className="flex-1 px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center justify-center gap-2 min-h-[44px]"
                style={{
                  borderRadius: "var(--radius-button)",
                  fontSize: "var(--text-base)",
                }}
              >
                <Dismiss20Regular className="w-5 h-5" />
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmCrop}
                className="flex-1 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center justify-center gap-2 min-h-[44px]"
                style={{
                  borderRadius: "var(--radius-button)",
                  fontSize: "var(--text-base)",
                }}
              >
                <Checkmark20Regular className="w-5 h-5" />
                Confirmar Corte
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
